import { useMemo, useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";

// Mexican-inspired constellations. Each is a sequence of normalized [x,y]
// points (0–100 viewBox). Lines connect consecutive points.
const CONSTELLATIONS: { name: string; points: [number, number][] }[] = [
  // Jaguar
  { name: "jaguar", points: [[6, 22], [11, 18], [16, 21], [20, 19], [23, 24], [20, 30], [15, 31], [10, 29], [7, 26]] },
  // Pyramid (Chichén Itzá)
  { name: "piramide", points: [[78, 78], [82, 70], [86, 62], [90, 70], [94, 78], [78, 78]] },
  // Quetzal
  { name: "quetzal", points: [[70, 12], [74, 15], [78, 13], [80, 18], [83, 24], [80, 28], [78, 22], [74, 20]] },
  // Mariposa monarca
  { name: "monarca", points: [[40, 70], [44, 66], [48, 70], [52, 66], [56, 70], [52, 74], [48, 70], [44, 74], [40, 70]] },
  // Ballena
  { name: "ballena", points: [[8, 78], [14, 76], [22, 77], [30, 80], [34, 76], [32, 82], [26, 83], [18, 82], [10, 81]] },
  // Tortuga marina
  { name: "tortuga", points: [[62, 40], [66, 36], [72, 36], [76, 40], [74, 46], [68, 48], [64, 46], [62, 40]] },
  // Volcán
  { name: "volcan", points: [[18, 50], [22, 42], [26, 46], [30, 40], [34, 50]] },
  // Agave
  { name: "agave", points: [[88, 38], [90, 30], [86, 34], [92, 36], [88, 28], [84, 32]] },
  // Templo maya
  { name: "templo", points: [[44, 18], [48, 14], [52, 14], [56, 18], [56, 24], [44, 24], [44, 18]] },
];

// Static seeded pseudo-random stars (deterministic across renders)
function makeStars(count: number, seed: number) {
  const out: { x: number; y: number; r: number; o: number; delay: number }[] = [];
  let s = seed;
  const rnd = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  for (let i = 0; i < count; i++) {
    out.push({
      x: rnd() * 100,
      y: rnd() * 100,
      r: 0.08 + rnd() * 0.35,
      o: 0.25 + rnd() * 0.65,
      delay: rnd() * 6,
    });
  }
  return out;
}

/**
 * Living Mexican universe background.
 * Layers (back → front): nebula gradients · star dust · stars · constellations.
 * Everything is GPU-friendly SVG/CSS; no canvas.
 */
export function StarField() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [mouse, setMouse] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    let raf = 0;
    const onMove = (e: MouseEvent) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        setMouse({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100,
        });
      });
    };
    const onLeave = () => setMouse(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, []);

  const stars = useMemo(() => makeStars(140, 7), []);
  const dust = useMemo(() => makeStars(80, 23), []);

  return (
    <div
      ref={wrapRef}
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden
    >
      {/* Deep space base */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 45%, #0c1124 0%, #06080f 55%, #03050a 100%)",
        }}
      />

      {/* Nebulae — slow drifting blurs in Mexican palette */}
      <motion.div
        className="absolute -left-[15%] top-[5%] h-[70vh] w-[70vh] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,140,60,0.22), rgba(255,140,60,0) 65%)",
          filter: "blur(60px)",
        }}
        animate={{ x: [0, 30, 0], y: [0, 20, 0] }}
        transition={{ duration: 60, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute right-[-12%] top-[20%] h-[60vh] w-[60vh] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(180,80,200,0.18), rgba(180,80,200,0) 65%)",
          filter: "blur(80px)",
        }}
        animate={{ x: [0, -25, 0], y: [0, 30, 0] }}
        transition={{ duration: 80, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-[-15%] left-[25%] h-[65vh] w-[65vh] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(60,160,200,0.16), rgba(60,160,200,0) 65%)",
          filter: "blur(70px)",
        }}
        animate={{ x: [0, 35, 0], y: [0, -20, 0] }}
        transition={{ duration: 90, repeat: Infinity, ease: "easeInOut" }}
      />

      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 h-full w-full"
      >
        <defs>
          <radialGradient id="starGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(255,235,200,1)" />
            <stop offset="100%" stopColor="rgba(255,235,200,0)" />
          </radialGradient>
          <filter id="constGlow">
            <feGaussianBlur stdDeviation="0.35" />
          </filter>
        </defs>

        {/* Star dust — tiny, very faint */}
        {dust.map((d, i) => (
          <circle
            key={`d${i}`}
            cx={d.x}
            cy={d.y}
            r={d.r * 0.4}
            fill="#ffffff"
            opacity={d.o * 0.3}
          />
        ))}

        {/* Stars — pulsing softly */}
        {stars.map((s, i) => (
          <circle
            key={`s${i}`}
            cx={s.x}
            cy={s.y}
            r={s.r}
            fill="#ffffff"
            opacity={s.o}
            style={{
              animation: `twinkle ${4 + (i % 5)}s ease-in-out ${s.delay}s infinite`,
            }}
          />
        ))}

        {/* Constellations — light up when the cursor gets close */}
        {CONSTELLATIONS.map((c) => {
          const cx = c.points.reduce((a, p) => a + p[0], 0) / c.points.length;
          const cy = c.points.reduce((a, p) => a + p[1], 0) / c.points.length;
          const near =
            mouse &&
            Math.hypot(mouse.x - cx, mouse.y - cy) < 18;
          const path =
            "M " + c.points.map((p) => `${p[0]} ${p[1]}`).join(" L ");
          return (
            <g
              key={c.name}
              style={{
                transition: "opacity 800ms ease-out",
                opacity: near ? 1 : 0.45,
              }}
            >
              <path
                d={path}
                fill="none"
                stroke={near ? "rgba(255,210,140,0.85)" : "rgba(255,255,255,0.18)"}
                strokeWidth={near ? 0.18 : 0.1}
                strokeLinecap="round"
                strokeLinejoin="round"
                filter={near ? "url(#constGlow)" : undefined}
                style={{ transition: "stroke 800ms ease-out, stroke-width 800ms ease-out" }}
              />
              {c.points.map((p, i) => (
                <circle
                  key={i}
                  cx={p[0]}
                  cy={p[1]}
                  r={near ? 0.55 : 0.35}
                  fill={near ? "#ffd89e" : "#ffffff"}
                  opacity={near ? 1 : 0.7}
                  style={{ transition: "all 800ms ease-out" }}
                />
              ))}
            </g>
          );
        })}
      </svg>

      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: var(--o, 0.5); }
          50% { opacity: 0.95; }
        }
      `}</style>
    </div>
  );
}
